import {useEffect,useMemo,useState} from 'react';

type Kind='customers'|'memberships'|'gift-cards'|'service-credits';
type Location={code:string;name:string;timezone:string};
type Report={
  kind:Kind;headers:string[];rows:Record<string,string>[];
  summary:{count:number;availableServiceUses:number|null;
    activeMemberships:number|null};
  scope:'VERIFIED_PWA_RECORDS_ONLY';includesHistoricalHelm:false;
  accuracyNote:string;
};
const setup:Record<Kind,{name:string;date:string;status:{key:string;label:string}[];
  columns:{label:string;key:string}[];filename:string}>={
  customers:{
    name:'Customers',date:'Customer Since',
    status:[{key:'all',label:'All Statuses'},{key:'active',label:'Active'},
      {key:'disabled',label:'Disabled'}],
    columns:[
      {label:'Customer',key:'Customer First Name'},
      {label:'Email',key:'Email'},{label:'Phone',key:'Phone'},
      {label:'Birthday',key:'Birthday'},
      {label:'Since',key:'Customer Since'},
      {label:'Visits',key:'Past Visits'},
      {label:'Last Visit',key:'Last Visit'},
      {label:'Unused Service Credits',key:'Unused Service Credits'},
      {label:'Active Memberships',key:'Active Memberships'},
      {label:'Total Spent',key:'Total Spent'},
    ],filename:'customers',
  },
  memberships:{
    name:'Memberships',date:'Sold within',
    status:[{key:'active',label:'Active'},{key:'all',label:'All'},
      {key:'pending',label:'Pending'},{key:'ended',label:'Ended'}],
    columns:[
      {label:'Member',key:'Member First Name'},{label:'Type',key:'Type'},
      {label:'Price',key:'Recurring Price'},{label:'Status',key:'Status'},
      {label:'Begun',key:'Begun'},{label:'Charges',key:'Charges'},
      {label:'Last Charge',key:'Last Charge'},
      {label:'Last Use',key:'Last Use'},
      {label:'Last Visit',key:'Last Visit'},
      {label:'Unused',key:'Unused'},{label:'Total Spent',key:'Total Spent'},
    ],filename:'memberships',
  },
  'gift-cards':{
    name:'Gift Cards',date:'Created',
    status:[{key:'all',label:'All Balances'},
      {key:'available',label:'Available'},{key:'redeemed',label:'Redeemed'},
      {key:'pending',label:'Pending Delivery'}],
    columns:[
      {label:'Created',key:'Created'},{label:'Code & Series',key:'Code'},
      {label:'Series',key:'Series'},
      {label:'Service Credit Balance',key:'Service Credit Balance'},
      {label:'Purchased By',key:'Customer First Name'},
      {label:'Recipient',key:'Recipient Name'},
    ],filename:'gift-cards',
  },
  'service-credits':{
    name:'Service Credits',date:'Sold within',
    status:[{key:'all',label:'All Statuses'},{key:'available',label:'Available'},
      {key:'used',label:'Used'},{key:'expired',label:'Expired'}],
    columns:[
      {label:'Sold',key:'Sold'},{label:'Service',key:'Type'},
      {label:'Owner',key:'Owner Name'},{label:'Claimed',key:'Claimed On'},
      {label:'Owner Last Visit',key:'Owner Last Visit'},
      {label:'Source',key:'Source'},{label:'Used On',key:'Used On'},
      {label:'Expires',key:'Expires On'},
      {label:'Price',key:'Price'},
      {label:'Discount',key:'Discount'},
      {label:'Paid',key:'Paid'},
    ],filename:'service-credits',
  },
};
function dateToday(zone:string){
  const p=new Intl.DateTimeFormat('en-CA',{timeZone:zone,
    year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(new Date());
  const x=(key:string)=>p.find(y=>y.type===key)?.value??'';
  return x('year')+'-'+x('month')+'-'+x('day');
}
function startDate(zone:string){
  const now=new Date();now.setUTCDate(now.getUTCDate()-61);
  const p=new Intl.DateTimeFormat('en-CA',{timeZone:zone,
    year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(now);
  const x=(key:string)=>p.find(y=>y.type===key)?.value??'';
  return x('year')+'-'+x('month')+'-'+x('day');
}
const dateDisplay=(v:string,zone:string)=>{
  if(!v)return '';
  const date=new Date(v);
  return Number.isFinite(date.getTime())?
    new Intl.DateTimeFormat('en-US',{month:'numeric',day:'numeric',
      year:'2-digit',timeZone:zone}).format(date):v;
};
const view=(kind:Kind,key:string,value:string,zone:string)=>{
  if(!value)return '';
  if(['Customer Since','Begun','Created','Sold','Last Charge',
    'Used On','Expires On','Claimed On','Last Visit','Last Use',
    'Owner Last Visit'].includes(key))return dateDisplay(value,zone);
  if(kind==='gift-cards'&&key==='Code'&&value.length>24)
    return value.slice(0,8)+'…';
  return value;
};
/** Exact Helm report names; only source-verified PWA rows are displayed.
 * All 32/28/16 CSV columns stay in original owner-export order.
 */
export function OperationalReportsWorkspace({kind,location,locations,
  onChooseLocation,onCalendar}:{
  kind:Kind;location:Location;locations:Location[];
  onChooseLocation:(code:string)=>void;onCalendar:()=>void;
}){
  const config=setup[kind];
  const [from,setFrom]=useState(()=>{
    return kind==='gift-cards'||kind==='service-credits'?
      startDate(location.timezone):'';
  });
  const [to,setTo]=useState(()=>{
    return kind==='gift-cards'||kind==='service-credits'?
      dateToday(location.timezone):'';
  });
  const [status,setStatus]=useState(kind==='memberships'?'active':'all');
  const [search,setSearch]=useState('');
  const [report,setReport]=useState<Report|null>(null);
  const [state,setState]=useState<'loading'|'ready'|'error'>('loading');
  const [message,setMessage]=useState('');
  const [exporting,setExporting]=useState(false);
  const params=useMemo(()=>{
    const p=new URLSearchParams({location:location.code,status,search});
    if(from)p.set('from',from);
    if(to)p.set('to',to);
    return p;
  },[location.code,status,search,from,to]);
  useEffect(()=>{
    if(from&&to&&from>to){
      setState('error');setMessage('From date must be before To date.');
      return;
    }
    const ctrl=new AbortController();
    setState('loading');setMessage('');
    void fetch('/api/v1/staff/reports/'+kind+'?'+params,{
      credentials:'same-origin',cache:'no-store',signal:ctrl.signal,
    }).then(async response=>{
      const body=await response.json() as {ok?:boolean;data?:Report;
        error?:{code?:string}};
      if(!response.ok||!body.ok||body.data?.kind!==kind||
        body.data.scope!=='VERIFIED_PWA_RECORDS_ONLY'||
        body.data.includesHistoricalHelm!==false)
        throw Error(body.error?.code??'Report unavailable');
      if(!ctrl.signal.aborted){setReport(body.data);setState('ready');}
    }).catch(e=>{
      if(!ctrl.signal.aborted){
        setState('error');
        setMessage(e instanceof Error?e.message:'Report unavailable');
      }
    });
    return ()=>ctrl.abort();
  },[kind,params,from,to]);
  const exportCsv=async()=>{
    if(state!=='ready'||!report||exporting)return;
    setExporting(true);setMessage('');
    try{
      const q=new URLSearchParams(params);q.set('format','csv');
      const response=await fetch('/api/v1/staff/reports/'+kind+'?'+q,{
        credentials:'same-origin',cache:'no-store',
      });
      if(!response.ok)throw Error('Report CSV unavailable');
      const blob=await response.blob(),url=URL.createObjectURL(blob);
      const a=document.createElement('a');a.href=url;
      a.download=config.filename+(from?'-from-'+from:'')+
        (to?'-to-'+to:'')+'.csv';
      document.body.append(a);a.click();a.remove();
      window.setTimeout(()=>URL.revokeObjectURL(url),1000);
    }catch(e){setMessage(e instanceof Error?e.message:'CSV export failed');}
    finally{setExporting(false);}
  };
  const types=useMemo(()=>{
    if(kind!=='memberships'||!report)return [];
    const tally=new Map<string,number>();
    for(const row of report.rows){
      const plan=row.Type;if(!plan)continue;
      tally.set(plan,(tally.get(plan)??0)+1);
    }
    return Array.from(tally.entries()).sort((a,b)=>a[0].localeCompare(b[0]));
  },[kind,report]);
  return <section className="ecfs-reservations-report ecfs-operational-report"
    aria-label={config.name+' Report'}>
    <header className="ecfs-reservation-report-title">
      <h2>{config.name}{kind==='service-credits'?
        ' at East Coast Float Spa '+location.name:''}</h2>
      <button type="button" onClick={onCalendar}>Calendar</button>
    </header>
    <div className="ecfs-reservation-report-filters">
      <label>Location
        <select aria-label={config.name+' location'} value={location.code}
          onChange={e=>onChooseLocation(e.target.value)}>
          {locations.map(l=><option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
      </label>
      <label>{config.date} from
        <input type="date" aria-label={config.name+' from date'} value={from}
          onChange={e=>setFrom(e.target.value)}/></label>
      <label>To
        <input type="date" aria-label={config.name+' to date'} value={to}
          onChange={e=>setTo(e.target.value)}/></label>
      <label>Status
        <select aria-label={config.name+' status'} value={status}
          onChange={e=>setStatus(e.target.value)}>
          {config.status.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </label>
      <label>Search
        <input type="search" aria-label={'Search '+config.name}
          value={search} maxLength={100}
          onChange={e=>setSearch(e.target.value)}/></label>
      <button type="button" aria-label={'Export '+config.name+' CSV'}
        className="ecfs-report-csv"
        disabled={state!=='ready'||exporting}
        onClick={()=>void exportCsv()}>
        {exporting?'Exporting…':'CSV ↓'}
      </button>
    </div>
    {state==='loading'&&<p role="status">Loading {config.name.toLowerCase()}…</p>}
    {state==='error'&&<p role="alert">{message}</p>}
    {state==='ready'&&report&&<>
      <div className="ecfs-reservation-report-stats">
        <div><strong>{kind==='gift-cards'?'Gift Cards':kind==='memberships'?
          'Memberships':kind==='customers'?'Customers':'Service Credits'}</strong>
          <span>{report.summary.count.toLocaleString()}</span></div>
        {report.summary.availableServiceUses!==null&&
          <div><strong>Verified Available Service Uses</strong>
            <span>{report.summary.availableServiceUses.toLocaleString()}</span></div>}
        {report.summary.activeMemberships!==null&&
          <div><strong>Active PWA Memberships</strong>
            <span>{report.summary.activeMemberships.toLocaleString()}</span></div>}
      </div>
      <p className="ecfs-report-accuracy">{report.accuracyNote}</p>
      {kind==='memberships'&&types.length>0&&
        <div className="ecfs-operational-type-grid" aria-label="Membership types summary">
          {types.map(([name,count])=><div key={name}>
            <span>{name}</span><strong>{count}</strong></div>)}
        </div>}
      <div className="ecfs-report-table-scroll">
        <table className="ecfs-reservation-report-table">
          <thead><tr>{config.columns.map(c=>
            <th key={c.key}>{c.label}</th>)}</tr></thead>
          <tbody>{report.rows.map((r,i)=><tr key={r.id||r.Code||i}>
            {config.columns.map(c=><td key={c.key}>
              {view(kind,c.key,r[c.key]??'',location.timezone)}
            </td>)}</tr>)}</tbody>
        </table>
      </div>
      {report.rows.length===0&&<p role="status">
        No verified PWA {config.name.toLowerCase()} match these filters.
        This is not a count of historical FloatHelm records.
      </p>}
    </>}
    {state==='ready'&&message&&<p role="alert">{message}</p>}
  </section>;
}
